import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getPendingSizeSelectionApi, submitSizeSelectionApi } from 'api/services/uniformManagement';

const SizeSelectionModal = () => {
  const [visible, setVisible] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkPending();
  }, []);

  const checkPending = () => {
    getPendingSizeSelectionApi({}).then(response => {
      const items = response.data?.data || [];
      setPendingItems(items);
      if (items.length > 0) {
        setVisible(true);
        const initial = {};
        items.forEach(item => { initial[item.uniform_item] = null; });
        setSelections(initial);
      }
    }).catch(() => {});
  };

  const handleSizeChange = (uniformItemId, sizeOption) => {
    setSelections(prev => ({ ...prev, [uniformItemId]: sizeOption }));
  };

  const handleSkip = () => {
    setVisible(false);
  };

  const handleSubmit = () => {
    const selectionsArray = pendingItems
      .map(item => ({
        uniform_item: item.uniform_item,
        size: selections[item.uniform_item]?.value
      }))
      .filter(s => s.size);

    if (selectionsArray.length === 0) {
      toast.info('Please pick at least one size or skip for now', {
        position: "top-right", autoClose: 3000, transition: Bounce
      });
      return;
    }

    setSubmitting(true);
    submitSizeSelectionApi({ selections: selectionsArray }).then(response => {
      toast.success(response.data?.message || 'Sizes saved', {
        position: "top-right", autoClose: 3000, transition: Bounce
      });
      setSubmitting(false);
      setVisible(false);
      setPendingItems([]);
    }).catch(e => {
      setSubmitting(false);
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Failed to submit sizes';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  if (!visible || pendingItems.length === 0) return null;

  const anySelected = pendingItems.some(item => selections[item.uniform_item]);

  return (
    <Modal
      title="Select Your Uniform Sizes"
      open={visible}
      onCancel={handleSkip}
      maskClosable={false}
      keyboard={false}
      width={600}
      centered
      bodyStyle={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
      footer={[
        <button
          key="skip"
          type="button"
          className="btn btn-light"
          onClick={handleSkip}
          disabled={submitting}
        >
          Skip for now
        </button>,
        <Button
          key="save"
          label="Save Selections"
          icon="fa fa-check"
          className="btn btn-primary ms-2"
          loading={submitting}
          disabled={!anySelected || submitting}
          onClick={handleSubmit}
        />
      ]}
    >
      <p className="text-muted mb-3">
        Pick a size for each uniform below. You can skip for now and we&apos;ll remind you next time.
      </p>

      {pendingItems.map(item => {
        const sizeOptions = (item.available_sizes || []).map(s => ({
          label: `${s.name} (${s.code})`, value: s.id
        }));
        const genderLabel = item.uniform_item_gender === 'M' ? 'Male'
          : item.uniform_item_gender === 'F' ? 'Female' : 'Unisex';

        return (
          <div key={item.uniform_item} className="card mb-3">
            <div className="card-body p-3">
              <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-3">
                {item.uniform_item_image && (
                  <img
                    src={item.uniform_item_image}
                    alt={item.uniform_item_name}
                    style={{ width: 60, height: 60, objectFit: 'contain', flexShrink: 0 }}
                  />
                )}
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <h6 className="mb-1 text-truncate">{item.uniform_item_name}</h6>
                  <small className="text-muted">{genderLabel}</small>
                </div>
                <div style={{ minWidth: 180, width: '100%', maxWidth: 220 }}>
                  <FilterSelect
                    isClearable
                    onChange={(val) => handleSizeChange(item.uniform_item, val)}
                    optionLabel="label"
                    optionValue="value"
                    options={sizeOptions}
                    value={selections[item.uniform_item]}
                    placeholder="Select Size"
                    isLoading={false}
                    searchable={false}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </Modal>
  );
};

export default SizeSelectionModal;
